import type { PaginatedResponse } from '../types';
import type {
  DashboardDelinquentRow,
  DashboardNamedCount,
  DashboardSupportCancellationRow,
  DashboardSupportMetrics,
  DashboardSupportPaymentRow,
  DashboardSupportSnapshot,
  DashboardSupportStudentRow,
} from '../types/dashboard';
import { getBrazilCivilDate } from './competence';
import { matchesSearchText } from './searchText';

const EMPTY_METRICS: DashboardSupportMetrics = {
  newWithoutWorkout: 0,
  reactivated: 0,
  delinquent: 0,
  overdueCharges: 0,
  paused: 0,
  consultancyCredit: 0,
  problemPayments: 0,
  upcomingPayments: 0,
  cancellations: 0,
};

const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
);

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNullableString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return null;
};

const toStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
};

const mapMetrics = (raw: Record<string, unknown>): DashboardSupportMetrics => ({
  newWithoutWorkout: toNumber(raw.newWithoutWorkout),
  reactivated: toNumber(raw.reactivated),
  delinquent: toNumber(raw.delinquent),
  overdueCharges: toNumber(raw.overdueCharges),
  paused: toNumber(raw.paused),
  consultancyCredit: toNumber(raw.consultancyCredit),
  problemPayments: toNumber(raw.problemPayments),
  upcomingPayments: toNumber(raw.upcomingPayments),
  cancellations: toNumber(raw.cancellations),
});

const mapNamedCounts = (raw: unknown): DashboardNamedCount[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      const row = toRecord(item);
      const name = typeof row.name === 'string' ? row.name : '';
      if (!name) {
        return null;
      }

      return {
        name,
        value: toNumber(row.value),
      };
    })
    .filter((item): item is DashboardNamedCount => item != null);
};

const mapStudentRows = (raw: unknown): DashboardSupportStudentRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item): DashboardSupportStudentRow | null => {
      const row = toRecord(item);
      const studentName = typeof row.studentName === 'string' ? row.studentName : '';
      if (!studentName) {
        return null;
      }

      return {
        studentId: toNumber(row.studentId),
        studentName,
        studentEmail: toNullableString(row.studentEmail),
        payerNames: toStringList(row.payerNames),
        payerEmails: toStringList(row.payerEmails),
        coachName: typeof row.coachName === 'string' ? row.coachName : 'Sem professora',
        joinedAt: toNullableString(row.joinedAt),
        inactivatedAt: toNullableString(row.inactivatedAt),
        canceledAt: toNullableString(row.canceledAt),
        lastPaidAt: toNullableString(row.lastPaidAt),
        paymentSystems: toStringList(row.paymentSystems),
        periodEndsAt: toNullableString(row.periodEndsAt),
        daysOverdue: toNullableNumber(row.daysOverdue),
        followUpStage: toNullableString(row.followUpStage),
        followUpNotes: toNullableString(row.followUpNotes),
        overdueMonths: toNullableNumber(row.overdueMonths),
        overdueChargeCount: toNullableNumber(row.overdueChargeCount),
        oldestDueDate: toNullableString(row.oldestDueDate),
        latestDueDate: toNullableString(row.latestDueDate),
        totalOverdueValue: toNullableNumber(row.totalOverdueValue),
        reason: toNullableString(row.reason),
      };
    })
    .filter((item): item is DashboardSupportStudentRow => item != null);
};

const mapPaymentRows = (raw: unknown): DashboardSupportPaymentRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      const row = toRecord(item);
      const studentName = typeof row.studentName === 'string' ? row.studentName : '';
      if (!studentName) {
        return null;
      }

      return {
        paymentHistoryId: toNumber(row.paymentHistoryId),
        studentId: toNumber(row.studentId),
        studentName,
        coachName: typeof row.coachName === 'string' ? row.coachName : 'Sem professora',
        status: typeof row.status === 'string' ? row.status : '',
        dueDate: toNullableString(row.dueDate),
        value: toNullableNumber(row.value),
        paymentMethod: toNullableString(row.paymentMethod),
      };
    })
    .filter((item): item is DashboardSupportPaymentRow => item != null);
};

const mapCancellationRows = (raw: unknown): DashboardSupportCancellationRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      const row = toRecord(item);
      const studentName = typeof row.studentName === 'string' ? row.studentName : '';
      if (!studentName) {
        return null;
      }

      return {
        id: toNumber(row.id),
        studentId: toNumber(row.studentId),
        studentName,
        coachName: typeof row.coachName === 'string' ? row.coachName : 'Sem professora',
        canceledAt: toNullableString(row.canceledAt),
        reason: toNullableString(row.reason),
      };
    })
    .filter((item): item is DashboardSupportCancellationRow => item != null);
};

const toDateOnly = (value: string | null | undefined): string => (
  value ? value.slice(0, 10) : ''
);

export function filterPaymentsByDueDate<T extends { dueDate: string | null }>(
  rows: readonly T[],
  start: string,
  end: string,
): T[] {
  if (!start && !end) {
    return [...rows];
  }

  return rows.filter((row) => {
    const day = toDateOnly(row.dueDate);
    if (!day) {
      return false;
    }

    if (start && day < start) {
      return false;
    }

    if (end && day > end) {
      return false;
    }

    return true;
  });
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface DelinquentListFilters {
  searchName: string;
  paymentGateway: readonly string[];
  paidStart: string;
  paidEnd: string;
}

export type DelinquentFilterResult =
  | { ok: true; rows: DashboardSupportStudentRow[] }
  | { ok: false; message: string };

const paidOnBrazilDate = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (ISO_DATE.test(trimmed)) {
    return trimmed;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return getBrazilCivilDate(parsed);
};

const matchesDelinquentSearch = (row: DashboardSupportStudentRow, query: string): boolean => {
  const fields = [
    row.studentName,
    row.studentEmail ?? '',
    ...(row.payerNames ?? []),
    ...(row.payerEmails ?? []),
  ];

  return fields.some((field) => matchesSearchText(field, query));
};

export function getDelinquentPaidRangeError(paidStart: string, paidEnd: string): string | null {
  const start = paidStart.trim();
  const end = paidEnd.trim();
  const hasStart = Boolean(start);
  const hasEnd = Boolean(end);

  if (hasStart !== hasEnd) {
    return 'Informe a data inicial e a data final do pagamento.';
  }

  if (hasStart && hasEnd && (!ISO_DATE.test(start) || !ISO_DATE.test(end) || start > end)) {
    return 'Informe um período de pagamento válido.';
  }

  return null;
}

const readPositiveInt = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    return null;
  }

  return value;
};

const readNonNegativeInt = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    return null;
  }

  return value;
};

const mapDelinquentRow = (value: unknown): DashboardDelinquentRow | null => {
  const row = toRecord(value);
  const studentId = readPositiveInt(row.studentId);
  if (studentId == null) {
    return null;
  }

  return {
    studentId,
    studentName: typeof row.studentName === 'string' ? row.studentName : '',
    lastPaidAt: toNullableString(row.lastPaidAt),
    paymentSystems: toStringList(row.paymentSystems),
    daysOverdue: toNullableNumber(row.daysOverdue),
    followUpStage: toNullableString(row.followUpStage),
    followUpNotes: toNullableString(row.followUpNotes),
  };
};

export function mapDashboardDelinquentPage(raw: unknown): PaginatedResponse<DashboardDelinquentRow> | null {
  const payload = toRecord(raw);
  const pagination = toRecord(payload.pagination);
  const page = readPositiveInt(pagination.page);
  const pageSize = readPositiveInt(pagination.pageSize);
  const totalRecords = readNonNegativeInt(pagination.totalRecords);
  const totalPages = readNonNegativeInt(pagination.totalPages);

  if (
    page == null
    || pageSize == null
    || totalRecords == null
    || totalPages == null
    || typeof pagination.hasNextPage !== 'boolean'
    || typeof pagination.hasPreviousPage !== 'boolean'
    || !Array.isArray(payload.data)
  ) {
    return null;
  }

  return {
    pagination: {
      page,
      pageSize,
      totalRecords,
      totalPages,
      hasNextPage: pagination.hasNextPage,
      hasPreviousPage: pagination.hasPreviousPage,
    },
    data: payload.data
      .map(mapDelinquentRow)
      .filter((row): row is DashboardDelinquentRow => row != null),
  };
}

export function filterDelinquentRows(
  rows: readonly DashboardSupportStudentRow[],
  filters: DelinquentListFilters,
): DelinquentFilterResult {
  const paidRangeError = getDelinquentPaidRangeError(filters.paidStart, filters.paidEnd);
  if (paidRangeError) {
    return { ok: false, message: paidRangeError };
  }

  const paidStart = filters.paidStart.trim();
  const paidEnd = filters.paidEnd.trim();
  const hasStart = Boolean(paidStart);
  const hasEnd = Boolean(paidEnd);

  const query = filters.searchName.trim();
  const gateways = filters.paymentGateway.map((value) => value.toLowerCase());

  const filtered = rows.filter((row) => {
    if (query && !matchesDelinquentSearch(row, query)) {
      return false;
    }

    if (gateways.length > 0) {
      const systems = (row.paymentSystems ?? []).map((value) => value.toLowerCase());
      if (!systems.some((system) => gateways.includes(system))) {
        return false;
      }
    }

    if (hasStart && hasEnd) {
      const paidOn = paidOnBrazilDate(row.lastPaidAt);
      if (!paidOn || paidOn < paidStart || paidOn > paidEnd) {
        return false;
      }
    }

    return true;
  });

  return { ok: true, rows: filtered };
}

export function mapDashboardSupportSnapshot(raw: unknown): DashboardSupportSnapshot {
  const payload = toRecord(raw);
  const metricsRaw = toRecord(payload.metrics);

  return {
    generatedAt: typeof payload.generatedAt === 'string' ? payload.generatedAt : new Date().toISOString(),
    competence: typeof payload.competence === 'string' ? payload.competence : '',
    metrics: Object.keys(metricsRaw).length > 0 ? mapMetrics(metricsRaw) : EMPTY_METRICS,
    attention: mapNamedCounts(payload.attention),
    paymentHealth: mapNamedCounts(payload.paymentHealth),
    newWithoutWorkout: mapStudentRows(payload.newWithoutWorkout),
    reactivated: mapStudentRows(payload.reactivated),
    delinquent: mapStudentRows(payload.delinquent),
    overdueCharges: mapStudentRows(payload.overdueCharges),
    paused: mapStudentRows(payload.paused),
    consultancyCredit: mapStudentRows(payload.consultancyCredit),
    problemPayments: mapPaymentRows(payload.problemPayments),
    upcomingPayments: mapPaymentRows(payload.upcomingPayments),
    cancellations: mapCancellationRows(payload.cancellations),
  };
}
